import { useCallback, useEffect, useRef } from 'react';
import { useRunDetectionMutation } from '../../app/api';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { store } from '../../app/store';
import { apiErrorMessage } from '../../lib/validation';
import { useSelectedProject } from '../projects/useSelectedProject';
import { alertsAppended } from '../alerts/alertsSlice';
import { frozen, idled, tickAdvanced, tickFailed } from './detectionSlice';

/** Live tick cadence and single-shot cooldown, in ms. */
const TICK_MS = 5000;
/** Single-shot cooldown before a card can refetch. Shared with the card countdown. */
export const FREEZE_MS = 5000;

/**
 * The client detection engine — mounted once (in Dashboard). It owns all timers
 * and reconciles them against the per-rule runtime in the detection slice:
 * a rule that becomes `running` gets an immediate tick + a 5s interval; leaving
 * `running` clears it; `fetching` runs one guarded tick; `frozen` schedules the
 * cooldown → idle. Cards never touch timers — they only dispatch runtime actions
 * (see useRuleControl), so there is exactly one authority driving requests.
 *
 * Each tick reads fresh state via the store singleton (no stale closures),
 * computes `[from, to]` (`cursorIso ?? now-windowMs` for live, `now-windowMs`
 * for single-shot), calls the stateless `runDetection`, and — after the request
 * resolves — re-checks the rule's status so a response landing after Stop/Pause
 * is discarded instead of resurrecting cleared alerts or the cursor.
 */
export function useDetectionEngine(): void {
  const dispatch = useAppDispatch();
  const [runDetection] = useRunDetectionMutation();
  const runtime = useAppSelector((s) => s.detection.runtime);
  const project = useSelectedProject();

  // Fresh project for each tick without re-creating timers on project change.
  const projectRef = useRef(project);
  projectRef.current = project;

  const timers = useRef(new Map<string, number>()); // live setInterval handles
  const freezes = useRef(new Map<string, number>()); // single-shot setTimeout handles
  const inFlight = useRef(new Set<string>()); // per-rule overlap guard

  const runTick = useCallback(
    async (ruleId: string, mode: 'live' | 'single') => {
      if (inFlight.current.has(ruleId)) return;
      const rt = store.getState().detection.runtime[ruleId];
      if (!rt) return;

      const proj = projectRef.current;
      const now = Date.now();
      // The initial tick's window is the custom range if set, else the relative
      // `now - windowMs`. A live rule that has already ticked (cursorIso set) then
      // tracks the PRESENT: its window is [max(cursor, now - windowMs), now]. The
      // `now - windowMs` floor is what makes a custom-range live rule behave right —
      // after the one-off historical backfill (first tick = the custom range), the
      // cursor sits in the past; without the floor the next tick would span
      // [custom.to, now] and blow past the 31-day cap. With it, live jumps forward
      // and rolls a small window near now. A preset-window live rule is unaffected:
      // cursor ≈ now already, so the floor is a no-op and ticks advance ~one cadence.
      const resuming = mode === 'live' && rt.cursorIso;
      const fromMs = resuming
        ? Math.max(Date.parse(rt.cursorIso!), now - rt.windowMs)
        : rt.custom
          ? Date.parse(rt.custom.from)
          : now - rt.windowMs;
      const toMs = resuming
        ? now
        : rt.custom
          ? Date.parse(rt.custom.to)
          : now;
      const from = new Date(fromMs).toISOString();
      const to = new Date(toMs).toISOString();
      // Operator param overrides; the backend merges them over the rule defaults.
      const params = store.getState().rulesUi.params[ruleId];

      inFlight.current.add(ruleId);
      try {
        const anomalies = await runDetection({
          ruleId,
          params,
          from,
          to,
          projectId: proj?.id,
          polygon: proj?.polygon,
        }).unwrap();

        // Session guard: discard the result if Stop/Pause landed while in flight.
        const status = store.getState().detection.runtime[ruleId]?.status;
        if (mode === 'live' && status !== 'running') return;
        if (mode === 'single' && status !== 'fetching') return;

        // `ranAt` is the wall-clock completion time, NOT the window's `to`: a custom
        // `to` can be far in the past, which would break the freeze countdown (it
        // keys off lastRunAt) and misreport "last run". `to` stays the cursor.
        const ranAt = new Date().toISOString();
        // Live ticks accumulate (merge/dedupe); a single-shot Fetch replaces the
        // rule's group with just this run so a second Fetch doesn't stack a duplicate
        // card (its id embeds the window, so merge would never dedupe it away).
        dispatch(alertsAppended({ ruleId, anomalies, replace: mode === 'single' }));
        if (mode === 'live') dispatch(tickAdvanced({ ruleId, cursorIso: to, ranAt }));
        else dispatch(frozen({ ruleId, ranAt }));
      } catch (e) {
        // Session guard, same as the success path: a failure landing after Stop/Pause
        // must not record an error on a rule that's no longer in its expected state.
        const status = store.getState().detection.runtime[ruleId]?.status;
        if (mode === 'live' && status !== 'running') return;
        if (mode === 'single' && status !== 'fetching') return;
        const ranAt = new Date().toISOString();
        dispatch(tickFailed({ ruleId, error: apiErrorMessage(e, 'Detection request failed'), ranAt }));
        // A single-shot must still enter cooldown so its card unfreezes.
        if (mode === 'single') dispatch(frozen({ ruleId, ranAt }));
      } finally {
        inFlight.current.delete(ruleId);
      }
    },
    [dispatch, runDetection],
  );

  // Reconcile timers ↔ desired runtime. Idempotent; re-runs whenever runtime changes.
  useEffect(() => {
    const liveTimers = timers.current;
    const freezeTimers = freezes.current;
    for (const [ruleId, rt] of Object.entries(runtime)) {
      const hasTimer = liveTimers.has(ruleId);
      if (rt.status === 'running' && !hasTimer) {
        void runTick(ruleId, 'live'); // immediate backfill / resume-gap tick
        liveTimers.set(ruleId, window.setInterval(() => void runTick(ruleId, 'live'), TICK_MS));
      } else if (rt.status !== 'running' && hasTimer) {
        clearInterval(liveTimers.get(ruleId)!);
        liveTimers.delete(ruleId);
      }

      if (rt.status === 'fetching' && !inFlight.current.has(ruleId)) {
        void runTick(ruleId, 'single');
      }

      if (rt.status === 'frozen' && !freezeTimers.has(ruleId)) {
        freezeTimers.set(
          ruleId,
          window.setTimeout(() => {
            freezeTimers.delete(ruleId);
            dispatch(idled(ruleId));
          }, FREEZE_MS),
        );
      }
    }
  }, [runtime, runTick, dispatch]);

  // Tear down every timer on unmount.
  useEffect(
    () => () => {
      timers.current.forEach((id) => clearInterval(id));
      freezes.current.forEach((id) => clearTimeout(id));
      timers.current.clear();
      freezes.current.clear();
    },
    [],
  );
}
